import { z } from "zod";
import { staffCatalogFulfilmentModes, staffCatalogItemStatuses, staffCatalogItemTypes, staffCatalogListItemSchema, staffCatalogPriceUnits, staffCatalogPricingModels, staffCatalogTagNamespaces, } from "./staff-catalog.js";
// The contract of staff catalog search (CHT-710): the route S1, `POST /staff/catalog/search`
// (capability `catalog.read_internal`). It is a POST because the request is a structured body, not
// because it changes anything: the route writes nothing. Same rules as `staff-catalog.ts`: every
// top-level shape is STRICT, the API validates the body with this schema, and the admin and the typed
// client validate the response with it.
//
// The body has no `audience`: the route is staff only. Rules BETWEEN parts of the request (a box with
// `west` before `east`, a price with `minMinor` above `maxMinor`, a `partySize` with a type it does not
// apply to) are not checked here. The use case answers them as `INVALID` or `CONFLICTING_CONSTRAINTS`
// with a reason code, so a caller learns which rule failed.
//
// This package cannot import the domain package. The tuples and bounds below repeat the domain's
// (`catalog-search.ts`); `apps/api` has a parity test that fails when one differs.
// --- codes (parity-tested against the domain) ------------------------------------------------------
export const staffCatalogSearchFilterNames = [
    "text",
    "types",
    "location",
    "near",
    "within",
    "price",
    "pricingModels",
    "dietary",
    "categories",
    "eventWindow",
    "partySize",
    "fulfilmentModes",
    "preferenceTags",
];
export const staffCatalogSearchOutcomes = [
    "OK",
    "NO_MATCH",
    "CONFLICTING_CONSTRAINTS",
    "INVALID",
];
export const staffCatalogSearchConflictCodes = [
    "price.min_gt_max",
    "event_window.from_not_before_to",
    "party_size.not_applicable",
    "event_window.not_applicable",
    "near.outside_within",
];
export const staffCatalogSearchInvalidCodes = [
    "text.too_many_tokens",
    "within.south_not_before_north",
    "within.west_not_before_east",
    "within.too_large",
    "event_window.too_long",
    "staff_field.not_allowed",
];
export const staffCatalogSearchNoticeCodes = ["NO_TEXT_MATCH"];
export const staffCatalogSearchReasonCodes = [
    ...staffCatalogSearchConflictCodes,
    ...staffCatalogSearchInvalidCodes,
    ...staffCatalogSearchNoticeCodes,
];
export const staffCatalogSearchSemanticStates = ["USED", "UNAVAILABLE", "NOT_REQUESTED"];
export const staffCatalogSearchEmbeddingStates = [
    "NONE",
    "PENDING",
    "READY",
    "STALE",
    "FAILED",
];
export const staffCatalogSearchHitReasons = [
    "NAME_EXACT",
    "NAME_PHRASE",
    "NAME_PREFIX",
    "TEXT_MATCH",
    "SEMANTIC_MATCH",
    "WITHIN_RADIUS",
    "EDITORIAL_BOOST",
    "FRESHNESS_BOOST",
    "PREFERENCE_BOOST",
];
export const staffCatalogSearchCollapseReasons = ["SAME_NAME", "SIMILAR_VECTOR"];
export const staffCatalogSearchVectorPaths = ["EXACT", "HNSW", "NONE"];
// --- bounds (parity-tested against the domain) -----------------------------------------------------
export const STAFF_CATALOG_SEARCH_BOUNDS = Object.freeze({
    textChars: 200,
    /** Word tokens after folding. The use case checks it (`text.too_many_tokens`); the schema cannot fold. */
    textTokens: 16,
    locality: 100,
    dietary: 10,
    categories: 10,
    preferenceTags: 5,
    codeChars: 64,
    limitDefault: 20,
    limitMax: 50,
    radiusMinMeters: 100,
    radiusMaxMeters: 200_000,
    partySizeMax: 100,
});
// --- path -------------------------------------------------------------------------------------------
export const STAFF_CATALOG_SEARCH_PATH = "/staff/catalog/search";
// --- request (S1) -----------------------------------------------------------------------------------
const CODE = /^[a-z0-9_]{1,64}$/;
const COUNTRY = /^[A-Z]{2}$/;
const CURRENCY = /^[A-Z]{3}$/;
const code = z.string().regex(CODE);
const latitude = z.number().min(-90).max(90);
const longitude = z.number().min(-180).max(180);
const minorAmount = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const instant = z.iso.datetime({ offset: true });
const searchLocationSchema = z
    .strictObject({
    countryCode: z.string().regex(COUNTRY).optional(),
    locality: z.string().min(1).max(STAFF_CATALOG_SEARCH_BOUNDS.locality).optional(),
})
    .refine((value) => value.countryCode !== undefined || value.locality !== undefined, {
    message: "location needs a countryCode or a locality",
});
const searchNearSchema = z.strictObject({
    latitude,
    longitude,
    radiusMeters: z
        .number()
        .int()
        .min(STAFF_CATALOG_SEARCH_BOUNDS.radiusMinMeters)
        .max(STAFF_CATALOG_SEARCH_BOUNDS.radiusMaxMeters),
});
/** Ranges only. `west` before `east`, `south` before `north` and the size of the box are use-case rules. */
const searchWithinSchema = z.strictObject({
    south: latitude,
    west: longitude,
    north: latitude,
    east: longitude,
});
const searchPriceSchema = z.strictObject({
    currency: z.string().regex(CURRENCY),
    minMinor: minorAmount.optional(),
    maxMinor: minorAmount.optional(),
    unit: z.enum(staffCatalogPriceUnits).optional(),
    includeUnpriced: z.boolean().optional(),
});
const searchEventWindowSchema = z.strictObject({ from: instant, to: instant });
const searchPreferenceTagSchema = z.strictObject({
    namespace: z.enum(staffCatalogTagNamespaces),
    code,
});
/**
 * The body of S1. Every key is optional: a body with none lists the first PUBLISHED items by editorial
 * order. Unknown keys are refused.
 */
export const staffCatalogSearchBodySchema = z.strictObject({
    text: z.string().min(1).max(STAFF_CATALOG_SEARCH_BOUNDS.textChars).optional(),
    types: z.array(z.enum(staffCatalogItemTypes)).min(1).max(staffCatalogItemTypes.length).optional(),
    location: searchLocationSchema.optional(),
    near: searchNearSchema.optional(),
    within: searchWithinSchema.optional(),
    price: searchPriceSchema.optional(),
    pricingModels: z
        .array(z.enum(staffCatalogPricingModels))
        .min(1)
        .max(staffCatalogPricingModels.length)
        .optional(),
    dietary: z.array(code).min(1).max(STAFF_CATALOG_SEARCH_BOUNDS.dietary).optional(),
    categories: z.array(code).min(1).max(STAFF_CATALOG_SEARCH_BOUNDS.categories).optional(),
    eventWindow: searchEventWindowSchema.optional(),
    partySize: z.number().int().min(1).max(STAFF_CATALOG_SEARCH_BOUNDS.partySizeMax).optional(),
    fulfilmentModes: z
        .array(z.enum(staffCatalogFulfilmentModes))
        .min(1)
        .max(staffCatalogFulfilmentModes.length)
        .optional(),
    preferenceTags: z
        .array(searchPreferenceTagSchema)
        .min(1)
        .max(STAFF_CATALOG_SEARCH_BOUNDS.preferenceTags)
        .optional(),
    /** Default `["PUBLISHED"]`. */
    includeStatuses: z
        .array(z.enum(staffCatalogItemStatuses))
        .min(1)
        .max(staffCatalogItemStatuses.length)
        .optional(),
    /** 1 to 50, default 20. */
    limit: z.number().int().min(1).max(STAFF_CATALOG_SEARCH_BOUNDS.limitMax).optional(),
    /** Adds the `debug` block to the answer. */
    debug: z.boolean().optional(),
});
// --- response (S1) ----------------------------------------------------------------------------------
const count = z.number().int().min(0);
const filterName = z.enum(staffCatalogSearchFilterNames);
const searchReasonSchema = z.strictObject({
    code: z.enum(staffCatalogSearchReasonCodes),
    filters: z.array(filterName),
});
/** Computed for the answer, never stored. A rank is the position (from 1) in that signal, or `null`. */
export const staffCatalogSearchScoresSchema = z.strictObject({
    final: z.number(),
    fused: z.number(),
    nameTier: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
    lexicalRank: z.number().int().min(1).nullable(),
    vectorRank: z.number().int().min(1).nullable(),
    geoRank: z.number().int().min(1).nullable(),
    editorialBoost: z.number(),
    freshnessBoost: z.number(),
    preferenceBoost: z.number(),
});
export const staffCatalogSearchHitSchema = z
    .strictObject({
    item: staffCatalogListItemSchema,
    /** The same value as `item.status`. */
    status: z.enum(staffCatalogItemStatuses),
    /** Metres from `near`, a whole number. `null` without `near`. */
    distanceMeters: count.nullable(),
    scores: staffCatalogSearchScoresSchema,
    reasons: z.array(z.enum(staffCatalogSearchHitReasons)),
    embedding: z.strictObject({
        state: z.enum(staffCatalogSearchEmbeddingStates),
        modelKey: z.string().min(1).max(64).nullable(),
    }),
    /** Listings folded into this hit. */
    collapsed: z.array(z.strictObject({
        id: z.string().min(1).max(64),
        name: z.string(),
        reason: z.enum(staffCatalogSearchCollapseReasons),
    })),
    /** A string, not a literal: a new ranking version must not break an older client. */
    rankingVersion: z.string().min(1).max(16),
})
    .refine((hit) => hit.status === hit.item.status, {
    message: "status must equal item.status",
});
const blockingCountSchema = z.strictObject({
    filter: filterName,
    /** Items that pass every other filter and fail only this one. */
    itemsWithoutThisFilter: count,
});
export const staffCatalogSearchDebugSchema = z.strictObject({
    eligibleCount: count,
    nameCandidates: count,
    lexicalCandidates: count,
    vectorCandidates: count,
    geoCandidates: count,
    vectorPath: z.enum(staffCatalogSearchVectorPaths),
    embeddingModelKey: z.string().min(1).max(64).nullable(),
    blocking: z.array(blockingCountSchema),
    /** The embedding states of the eligible set, counted. Every state has a key. */
    embeddingStates: z.strictObject({
        NONE: count,
        PENDING: count,
        READY: count,
        STALE: count,
        FAILED: count,
    }),
});
/**
 * The answer of S1. `hits` is empty unless `outcome` is `OK`. `blockingFilters` is empty unless
 * `outcome` is `NO_MATCH`. `debug` is `null` unless the request set `debug`.
 */
export const staffCatalogSearchResultSchema = z.strictObject({
    outcome: z.enum(staffCatalogSearchOutcomes),
    reasons: z.array(searchReasonSchema),
    blockingFilters: z.array(filterName),
    notApplicable: z.array(filterName),
    semantic: z.enum(staffCatalogSearchSemanticStates),
    hits: z.array(staffCatalogSearchHitSchema).max(STAFF_CATALOG_SEARCH_BOUNDS.limitMax),
    debug: staffCatalogSearchDebugSchema.nullable(),
});
